import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  forwardRef,
  SimpleChanges,
  EventEmitter,
  Output,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { RxIconModule } from '@nrwl/rx-icon';
import { IMaskModule } from 'angular-imask';
import { CountryService } from '@nrwl/rx-service-country';

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'rx-input-intl-phone',
  templateUrl: './rx-input-intl-phone.component.html',
  styleUrls: ['./rx-input-intl-phone.component.scss'],
  imports: [CommonModule, FormsModule, RxIconModule, NgSelectModule, IMaskModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RxInputInternationalPhoneComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RxInputInternationalPhoneComponent),
      multi: true,
    },
  ],
})
export class RxInputInternationalPhoneComponent
  implements OnInit, ControlValueAccessor, OnChanges, Validator
{
  @Input() public placeholder = 'Phone';
  @Input() public countryCode: string;
  @Input() readonly = false;
  @Input()
  public set dialCode(value: string) {
    if (value) {
      this._dialCode = value;
      const telNumber = this.tel ? this.tel : '';
      const phone = `+${this._dialCode}${telNumber}`;
      if (this.tel) {
        this.onChange(phone);
        this._value = phone;
      }
    }
  }
  public get dialCode() {
    return this._dialCode;
  }
  @Input()
  public set value(phone: string) {
    this.parseNumber(phone);

    this._value = phone;
  }
  public get value() {
    return this._value;
  }

  @Output()
  public valueChange: EventEmitter<string> = new EventEmitter();

  private _dialCode: string;

  public countries: any = [];
  public mask: any = '(000) 000-0000';
  public tel: any = null;
  public phone: any = '';
  public control: FormControl | any;
  private _value: string | any;

  public onChange = (value: string) => {};
  public onTouched = () => {};

  constructor(private _countryService: CountryService) {}

  public get isValid(): boolean {
    return this.control && !this.control.pending && !this.control.errors && !this.control.invalid;
  }

  public ngOnChanges(changes: SimpleChanges | any): void {
    // trigger validation
    this.onChange(this.value);
  }

  // Register for change
  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  // Register for touch/click
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Update the model and changes
  public writeValue(value: string): void {
    if (this.value !== value) {
      this._value = value;

      if (value) {
        this.parseNumber(value);
      } else {
        this.tel = '';
        this.countryCode = '';
        this.dialCode = '';
      }

      this.onChange(this.value);
    }
  }

  // validate
  public validate(control: FormControl): ValidationErrors | any {
    this.control = control;
    return control.valid ? null : control.errors;
  }

  public ngOnInit() {
    this.countries = this._countryService.getCountries();
  }

  public changeCountry(event) {
    this._dialCode = event.dialCode;
    const telNumber = this.tel ? this.tel : '';
    this.phone = `+${this._dialCode}${telNumber}`;

    if (this.tel) {
      this.onChange(this.phone);
    }
  }

  public telChange() {
    const prefix = this._dialCode ? `+${this._dialCode}` : '';
    this.phone = `${prefix}${this.tel}`;

    this.onChange(this.phone);
  }

  public parseNumber(phone: string) {
    const phoneNumber = parsePhoneNumberFromString(phone);

    if (phoneNumber) {
      this.countryCode = phoneNumber.country ? phoneNumber.country.toLowerCase() : '';
      this._dialCode = String(phoneNumber.countryCallingCode);
      this.tel = String(phoneNumber.nationalNumber);
    }
  }

  public onBlur() {
    this.onTouched();

    if (this.isValid) {
      this.parseNumber(this.control.value);
    }
  }

  public onReset() {
    this.tel = '';
    this._value = '';
    this.countryCode = '';
    this.phone = '';
    this.dialCode = '';

    this.onChange(this.value);
  }
}
